import type { TemplatePriority, ChannelId, NotificationType } from "./types";

// ─── Priority Configuration ──────────────────────────────────────────────────

export const PRIORITY_OPTIONS: TemplatePriority[] = ["CRITICAL", "HIGH", "NORMAL", "LOW"];

/** i18n key per priority — resolve with t() at the render site. */
export const PRIORITY_LABEL_KEYS: Record<TemplatePriority, string> = {
  CRITICAL: "notifications.priority.CRITICAL",
  HIGH: "notifications.priority.HIGH",
  NORMAL: "notifications.priority.NORMAL",
  LOW: "notifications.priority.LOW",
};

export const PRIORITY_CONFIG: Record<TemplatePriority, { color: string; bg: string }> = {
  CRITICAL: { color: "text-purple-800 dark:text-purple-300", bg: "bg-purple-100 dark:bg-purple-900/30" },
  HIGH: { color: "text-red-800 dark:text-red-300", bg: "bg-red-100 dark:bg-red-900/30" },
  NORMAL: { color: "text-blue-800 dark:text-blue-300", bg: "bg-blue-100 dark:bg-blue-900/30" },
  LOW: { color: "text-muted-foreground", bg: "bg-muted" },
};

// ─── Channel Configuration ───────────────────────────────────────────────────

export const CHANNEL_IDS: ChannelId[] = ["general", "challenges", "events", "koins"];

/** i18n key per channel label — resolve with t() at the render site. */
export const CHANNEL_LABEL_KEYS: Record<ChannelId, string> = {
  general: "notifications.channel.general",
  challenges: "notifications.channel.challenges",
  events: "notifications.channel.events",
  koins: "notifications.channel.koins",
};

/** i18n key per channel description — resolve with t() at the render site. */
export const CHANNEL_DESCRIPTION_KEYS: Record<ChannelId, string> = {
  general: "notifications.channelDesc.general",
  challenges: "notifications.channelDesc.challenges",
  events: "notifications.channelDesc.events",
  koins: "notifications.channelDesc.koins",
};

export const CHANNEL_CONFIG: Record<ChannelId, { emoji: string }> = {
  general: { emoji: "🔔" },
  challenges: { emoji: "🏆" },
  events: { emoji: "📅" },
  koins: { emoji: "🪙" },
};

// ─── Notification Type Labels ────────────────────────────────────────────────

export const NOTIFICATION_TYPES: NotificationType[] = [
  "GENERAL",
  "CHALLENGE_STARTING",
  "CHALLENGE_ENDED",
  "EVENT_REMINDER",
  "KOINS_MILESTONE",
  "MARKETPLACE",
];

/** i18n key per notification type — resolve with t() at the render site. */
export const NOTIFICATION_TYPE_LABEL_KEYS: Record<NotificationType, string> = {
  GENERAL: "notifications.type.GENERAL",
  CHALLENGE_STARTING: "notifications.type.CHALLENGE_STARTING",
  CHALLENGE_ENDED: "notifications.type.CHALLENGE_ENDED",
  EVENT_REMINDER: "notifications.type.EVENT_REMINDER",
  KOINS_MILESTONE: "notifications.type.KOINS_MILESTONE",
  MARKETPLACE: "notifications.type.MARKETPLACE",
};

// ─── Deep Link Destinations (mirrors frontend handleNotificationDeepLink) ────

export interface DeepLinkDestination {
  id: string;
  /** i18n key — resolve with t() at the render site. */
  labelKey: string;
  screen: string;
  requiresId: boolean;
  /** i18n key — resolve with t() at the render site. */
  idLabelKey?: string;
  idPlaceholder?: string;
}

export const DEEP_LINK_DESTINATIONS: DeepLinkDestination[] = [
  { id: "none", labelKey: "notifications.destination.none", screen: "", requiresId: false },
  {
    id: "challenge-details",
    labelKey: "notifications.destination.challengeDetails",
    screen: "challenge-details",
    requiresId: true,
    idLabelKey: "notifications.destination.challengeIdLabel",
    idPlaceholder: "507f1f77bcf86cd799439011",
  },
  {
    id: "event-details",
    labelKey: "notifications.destination.eventDetails",
    screen: "event-details",
    requiresId: true,
    idLabelKey: "notifications.destination.eventIdLabel",
    idPlaceholder: "507f1f77bcf86cd799439012",
  },
  { id: "koins-history", labelKey: "notifications.destination.koinsHistory", screen: "koins-history", requiresId: false },
  { id: "marketplace", labelKey: "notifications.destination.marketplace", screen: "marketplace", requiresId: false },
];

// ─── Tap Destination Map (per template name) ─────────────────────────────────

export const TAP_DESTINATION_MAP: Record<string, { screen: string; labelKey: string }> = {
  "challenge.going-live": { screen: "/challenge-details/{id}", labelKey: "notifications.destination.challengeDetails" },
  "challenge.starting-soon": { screen: "/challenge-details/{id}", labelKey: "notifications.destination.challengeDetails" },
  "challenge.completed": { screen: "/challenge-details/{id}", labelKey: "notifications.destination.challengeDetails" },
  "event.going-live": { screen: "/event-details/{id}", labelKey: "notifications.destination.eventDetails" },
  "event.starting-soon": { screen: "/event-details/{id}", labelKey: "notifications.destination.eventDetails" },
  "koins.milestone": { screen: "/koins-history", labelKey: "notifications.destination.koinsHistory" },
};

export const MAX_BROADCAST_RECIPIENTS = 5000;
